import React, { useEffect, useRef, useState } from 'react';
import authService from '../services/auth';
import notify from '../services/notifications';
import { toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';

// 60 minutes idle timeout with warnings 3 and 2 minutes before
const IDLE_LIMIT_MS = 60 * 60 * 1000;
const WARNING_1_MS = 3 * 60 * 1000; // 3 minutes
const WARNING_2_MS = 2 * 60 * 1000; // 2 minutes

const SessionManager = () => {
  const lastActivityRef = useRef(Date.now());
  const timersRef = useRef({});
  const warnedRef = useRef({ w3: false, w2: false });
  const promptToastIdRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const countdownIntervalRef = useRef(null);

  const getExpiryAt = () => lastActivityRef.current + IDLE_LIMIT_MS;

  const startCountdown = () => {
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    const tick = () => {
      const remainMs = Math.max(0, getExpiryAt() - Date.now());
      setSecondsLeft(Math.ceil(remainMs / 1000));
    };
    tick();
    countdownIntervalRef.current = setInterval(tick, 1000);
  };

  const stopCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const showReauthPrompt = (minutesLeft) => {
    const content = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div>Session will expire in ~{minutesLeft} minutes due to inactivity.</div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={async () => {
              try {
                await authService.reauth();
                notify.success('Session extended');
                // Mark activity and reschedule
                lastActivityRef.current = Date.now();
                warnedRef.current = { w3: false, w2: false };
                scheduleTimers();
              } catch (e) {
                notify.error(e?.message || 'Failed to extend session');
              } finally {
                if (promptToastIdRef.current) toast.dismiss(promptToastIdRef.current);
                promptToastIdRef.current = null;
              }
            }}
            style={{ 
              padding: '6px 10px', 
              background: '#1976d2', 
              color: '#fff', 
              border: 0, 
              borderRadius: 6, 
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = '#1565c0'}
            onMouseLeave={(e) => e.target.style.background = '#1976d2'}
          >
            Stay signed in
          </button>
          <button
            onClick={async () => {
              if (promptToastIdRef.current) toast.dismiss(promptToastIdRef.current);
              promptToastIdRef.current = null;
              await authService.logout();
            }}
            style={{ 
              padding: '6px 10px', 
              background: '#e0e0e0', 
              color: '#333', 
              border: 0, 
              borderRadius: 6, 
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.background = '#d5d5d5'}
            onMouseLeave={(e) => e.target.style.background = '#e0e0e0'}
          >
            Logout now
          </button>
        </div>
      </div>
    );

    if (promptToastIdRef.current) {
      toast.update(promptToastIdRef.current, { render: content, autoClose: false, closeOnClick: false });
    } else {
      promptToastIdRef.current = toast.warning(content, { autoClose: false, closeOnClick: false });
    }
  };

  const setActivity = () => {
    lastActivityRef.current = Date.now();
    warnedRef.current = { w3: false, w2: false };
    scheduleTimers();
  };

  const scheduleTimers = () => {
    const clearTimers = () => {
      Object.values(timersRef.current).forEach((t) => t && clearTimeout(t));
      timersRef.current = {};
    };

    clearTimers();
    const now = Date.now();
    const expiryAt = getExpiryAt();
    const warn1At = expiryAt - WARNING_1_MS;
    const warn2At = expiryAt - WARNING_2_MS;

    if (warn1At > now) {
      timersRef.current.warn1 = setTimeout(() => {
        if (!warnedRef.current.w3) {
          showReauthPrompt(3);
          warnedRef.current.w3 = true;
        }
      }, warn1At - now);
    }

    if (warn2At > now) {
      timersRef.current.warn2 = setTimeout(() => {
        if (!warnedRef.current.w2) {
          // Close any toast and open blocking modal with countdown
          if (promptToastIdRef.current) {
            toast.dismiss(promptToastIdRef.current);
            promptToastIdRef.current = null;
          }
          setModalOpen(true);
          startCountdown();
          warnedRef.current.w2 = true;
        }
      }, warn2At - now);
    }

    if (expiryAt > now) {
      timersRef.current.expire = setTimeout(async () => {
        try {
          // Attempt seamless reauth to extend session if user is active now
          const idleElapsed = Date.now() - lastActivityRef.current;
          if (idleElapsed < WARNING_2_MS) {
            await authService.reauth();
            notify.success('Session extended');
            setActivity();
            return;
          }
        } catch (_) {}
        // If cannot reauth or still idle, log out
        notify.info('Session expired due to inactivity. Please sign in again.');
        await authService.logout();
      }, expiryAt - now);
    }
  };

  const handleStaySignedIn = async () => {
    try {
      await authService.reauth();
      notify.success('Session extended');
      setActivity();
      setModalOpen(false);
      stopCountdown();
      if (promptToastIdRef.current) {
        toast.dismiss(promptToastIdRef.current);
        promptToastIdRef.current = null;
      }
    } catch (e) {
      notify.error(e?.message || 'Failed to extend session');
    }
  };

  const handleLogoutNow = async () => {
    try {
      setModalOpen(false);
      stopCountdown();
    } finally {
      await authService.logout();
    }
  };

  useEffect(() => {
    const onAnyActivity = () => setActivity();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') setActivity();
    };

    // Track clicks, keypresses, uploads, scrolls
    window.addEventListener('click', onAnyActivity, { passive: true });
    window.addEventListener('keydown', onAnyActivity, { passive: true });
    window.addEventListener('scroll', onAnyActivity, { passive: true });
    window.addEventListener('dragend', onAnyActivity, { passive: true });
    window.addEventListener('drop', onAnyActivity, { passive: true });
    window.addEventListener('paste', onAnyActivity, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Initial schedule
    scheduleTimers();

    return () => {
      window.removeEventListener('click', onAnyActivity);
      window.removeEventListener('keydown', onAnyActivity);
      window.removeEventListener('scroll', onAnyActivity);
      window.removeEventListener('dragend', onAnyActivity);
      window.removeEventListener('drop', onAnyActivity);
      window.removeEventListener('paste', onAnyActivity);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      Object.values(timersRef.current).forEach((t) => t && clearTimeout(t));
      stopCountdown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open={modalOpen} onClose={() => {}} disableEscapeKeyDown fullWidth maxWidth="xs">
      <DialogTitle>Session expiring soon</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">
            Your session will expire due to inactivity.
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Expires in {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleLogoutNow} 
          color="inherit" 
          variant="outlined"
          sx={{
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderColor: 'rgba(0, 0, 0, 0.4)',
              transform: 'translateY(-1px)'
            },
            '&:active': {
              transform: 'translateY(1px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Logout now
        </Button>
        <Button 
          onClick={handleStaySignedIn} 
          color="primary" 
          variant="contained"
          sx={{
            cursor: 'pointer',
            '&:hover': {
              boxShadow: '0 4px 12px 0 rgba(33,150,243,0.20)',
              transform: 'translateY(-1px)'
            },
            '&:active': {
              transform: 'translateY(1px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionManager;


